// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/multi-select.js
import BaseComponent from "./base_component"
import { parseList } from "./dom"
import { setTemplate } from "./template"
import { createPopper } from "@popperjs/core"
let generatedId = 0

export default class MultiSelect extends BaseComponent {
  static componentName = "multi-select"
  static defaults = {
    cleaner: true,
    disabled: false,
    multiple: true,
    optionsStyle: "checkbox",
    placeholder: "Select...",
    search: false,
    selectAll: true,
    selectionType: "tags",
  }

  constructor(element, config = {}) {
    super(element, config)
    this.select = element
    this.originalName = element.getAttribute("name")
    this.originalRequired = element.required
    this.originalTabIndex = element.getAttribute("tabindex")
    this.optionData = new Map()
    this.originalMultiple = element.multiple
    this.config.multiple = this.config.multiple !== false
    this.select.multiple = this.config.multiple
    if (this.config.options) this.replaceOptions(this.config.options)
    this.applyInitialSelection()
    if (this.config.name) element.name = this.config.name
    element.required = Boolean(this.config.required || element.required)
    this.originalParent = element.parentNode
    this.originalNextSibling = element.nextSibling
    this.render()
    this.bindEvents()
  }

  isDisabled(option) {
    return option.disabled || option.parentElement?.disabled
  }

  externalSearch() {
    return parseList(this.config.search).includes("external")
  }

  hasSelectionLimit() {
    return this.config.multiple && this.config.selectionLimit != null
  }

  applyInitialSelection() {
    let count = 0
    const values = parseList(this.config.value).map(String)
    for (const option of this.select.options) {
      const selected = option.selected || values.includes(option.value)
      option.selected = Boolean(selected && (!this.hasSelectionLimit() || count < this.config.selectionLimit))
      if (option.selected) count++
    }
  }

  replaceOptions(options, preserveSelection = false) {
    const selected = preserveSelection
      ? this.selectedOptions.map((option) => {
          const clone = option.cloneNode(true)
          clone.disabled = this.isDisabled(option)
          return clone
        })
      : []
    const selectedValues = new Set(selected.map((option) => option.value))
    this.optionData.clear()
    this.select.replaceChildren()
    const append = (items, parent) =>
      items.forEach((option) => {
        if (option.options) {
          const group = document.createElement("optgroup")
          group.label = option.label
          group.disabled = Boolean(option.disabled)
          append(option.options, group)
          parent.append(group)
        } else {
          const native = new Option(
            option.text,
            String(option.value),
            false,
            Boolean(option.selected) || selectedValues.has(String(option.value)),
          )
          native.disabled = Boolean(option.disabled)
          this.optionData.set(native.value, option)
          parent.append(native)
        }
      })
    append(options, this.select)
    selected.forEach((option) => {
      if ([...this.select.options].some((loaded) => loaded.value === option.value)) return
      option.dataset.retainedSelection = "true"
      option.selected = true
      this.select.append(option)
    })
    if (preserveSelection && this.hasSelectionLimit()) {
      let count = selected.length
      for (const option of this.select.options) {
        if (!option.selected || selectedValues.has(option.value)) continue
        option.selected = count < this.config.selectionLimit
        if (option.selected) count++
      }
    }
    if (!this.config.multiple && !selected.length && ![...this.optionData.values()].some((option) => option.selected))
      this.select.selectedIndex = -1
  }

  update(config = {}) {
    this.config = { ...this.config, ...config }
    if (Object.prototype.hasOwnProperty.call(config, "name")) {
      if (config.name || this.originalName) this.select.name = config.name || this.originalName
      else this.select.removeAttribute("name")
    }
    if (!Object.prototype.hasOwnProperty.call(config, "options")) return
    const active = document.activeElement
    const hadFocus = this.wrapper.contains(active)
    const activeValue = active.dataset?.value
    this.replaceOptions(config.options, this.externalSearch())
    if (!this.externalSearch()) this.applyInitialSelection()
    this.options.replaceChildren()
    this.renderOptions(this.select, this.options)
    this.updateSelection()
    this.filter(this.search?.value || "", false)
    this.popper?.update()
    if (hadFocus && !this.wrapper.contains(document.activeElement)) {
      const option = this.visibleOptions().find((option) => option.dataset.value === activeValue)
      ;(option || this.search || this.toggler).focus({ preventScroll: true })
    }
  }

  nameControl() {
    const labelledBy = this.select.getAttribute("aria-labelledby")
    const label = this.select.getAttribute("aria-label")
    if (labelledBy) this.toggler.setAttribute("aria-labelledby", labelledBy)
    else if (label) this.toggler.setAttribute("aria-label", label)
    else {
      this.label = this.select.labels?.[0]
      if (!this.label && this.config.id)
        this.label = [...document.querySelectorAll("label")].find((label) => label.htmlFor === this.config.id)
      if (this.label) {
        if (!this.label.id) {
          this.generatedLabelId = `multi-select-label-${++generatedId}`
          this.label.id = this.generatedLabelId
        }
        this.toggler.setAttribute("aria-labelledby", this.label.id)
      }
    }
  }

  get selectedOptions() {
    const selected = [...this.select.options].filter((option) => option.selected)
    const values = selected.map((option) => option.value)
    this.selectionOrder = (this.selectionOrder || []).filter((value) => values.includes(value))
    for (const value of values) if (!this.selectionOrder.includes(value)) this.selectionOrder.push(value)
    return this.selectionOrder.map((value) => selected.find((option) => option.value === value))
  }

  render() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = this.select.className
    this.wrapper.classList.add("form-multi-select")
    this.select.classList.add("form-multi-select")
    if (this.config.disabled || this.select.disabled) this.wrapper.classList.add("disabled")
    this.select.before(this.wrapper)
    this.wrapper.append(this.select)
    this.select.tabIndex = -1

    this.toggler = document.createElement("div")
    this.toggler.className = "form-multi-select-input-group"
    this.toggler.setAttribute("role", "combobox")
    this.toggler.setAttribute("aria-expanded", "false")
    this.toggler.setAttribute("aria-haspopup", "listbox")
    if (!this.config.search) this.toggler.tabIndex = 0
    this.selection = document.createElement("div")
    this.selection.className = `form-multi-select-selection${this.config.multiple && this.config.selectionType === "tags" ? " form-multi-select-selection-tags" : ""}`
    this.selection.setAttribute("aria-live", "polite")
    this.toggler.append(this.selection)
    if (this.config.search) {
      this.search = document.createElement("input")
      this.search.className = "form-multi-select-search"
      this.search.setAttribute("aria-label", this.config.ariaSearchLabel || "Search options")
      this.search.autocomplete = "off"
      this.search.disabled = Boolean(this.config.disabled || this.select.disabled)
      if (this.config.id) this.search.id = this.config.id
      this.selection.append(this.search)
    }
    const buttons = document.createElement("div")
    buttons.className = "form-multi-select-buttons"
    if (this.config.cleaner) {
      this.cleaner = document.createElement("button")
      this.cleaner.type = "button"
      this.cleaner.className = "form-multi-select-cleaner"
      this.cleaner.setAttribute("aria-label", "Clear all selections")
      buttons.append(this.cleaner)
    }
    this.indicator = document.createElement("button")
    this.indicator.type = "button"
    this.indicator.className = "form-multi-select-indicator"
    this.indicator.setAttribute("aria-label", "Toggle visibility of options menu")
    buttons.append(this.indicator)
    this.toggler.append(buttons)
    this.wrapper.append(this.toggler)

    this.menu = document.createElement("div")
    this.menu.className = "form-multi-select-dropdown"
    if (this.config.headerTemplate || (this.config.multiple && this.config.selectAll)) {
      const header = document.createElement("div")
      header.className = "form-multi-select-dropdown-header"
      this.header = header
      this.selectAll = document.createElement("button")
      this.selectAll.type = "button"
      this.selectAll.className = `form-multi-select-all${this.config.optionsStyle === "checkbox" ? " form-multi-select-all-with-checkbox" : ""}`
      this.selectAll.textContent = "Select all"
      header.append(this.selectAll)
      this.menu.append(header)
    }
    this.options = document.createElement("div")
    this.options.className = "form-multi-select-options"
    this.options.id = `${this.select.id || `multi-select-${++generatedId}`}-listbox`
    this.options.setAttribute("role", "listbox")
    if (this.config.multiple) this.options.setAttribute("aria-multiselectable", "true")
    this.renderOptions(this.select, this.options)
    this.menu.append(this.options)
    this.wrapper.append(this.menu)
    this.toggler.setAttribute("aria-controls", this.options.id)
    this.nameControl()
    this.updateSelection()
  }

  renderOptions(source, parent) {
    ;[...source.children].forEach((child) => {
      if (child.tagName === "OPTGROUP") {
        const group = document.createElement("div")
        group.className = "form-multi-select-optgroup"
        const label = document.createElement("div")
        label.className = `form-multi-select-optgroup-label${child.disabled ? " disabled" : ""}`
        if (this.config.optionsGroupsTemplate)
          setTemplate(label, this.config.optionsGroupsTemplate({ label: child.label }))
        else label.textContent = child.label
        if (this.config.optionsGroupsSelectable && !child.disabled) {
          if (this.config.optionsGroupsStyle !== "text")
            label.classList.add("form-multi-select-optgroup-label-with-checkbox")
          label.tabIndex = 0
          label.setAttribute("role", "button")
          label.dataset.group = child.label
          label.setAttribute("aria-label", `Toggle ${child.label}`)
        }
        group.append(label)
        this.renderOptions(child, group)
        parent.append(group)
        return
      }
      if (child.tagName !== "OPTION" || child.dataset.retainedSelection) return
      const disabled = this.isDisabled(child)
      const option = document.createElement("div")
      option.className = `form-multi-select-option${this.config.optionsStyle === "checkbox" ? " form-multi-select-option-with-checkbox" : ""}${disabled ? " disabled" : ""}${child.selected ? " form-multi-selected" : ""}`
      option.dataset.value = child.value
      if (this.config.optionsTemplate)
        setTemplate(
          option,
          this.config.optionsTemplate(
            this.optionData.get(child.value) || { text: child.textContent, value: child.value },
          ),
        )
      else option.textContent = child.textContent
      option.tabIndex = disabled ? -1 : 0
      if (disabled) option.setAttribute("aria-disabled", "true")
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", child.selected)
      parent.append(option)
    })
  }

  bindEvents() {
    this.listen(this.toggler, "click", (event) => {
      if (this.config.disabled || this.select.disabled) return
      if (event.target.closest(".form-multi-select-cleaner")) return this.clear()
      if (this.search && this.selection.contains(event.target)) {
        this.show()
        this.search.focus()
      } else if (event.target.closest(".form-multi-select-indicator")) this.toggle()
      else this.show()
    })
    this.listen(this.options, "click", (event) => {
      const option = event.target.closest(".form-multi-select-option:not(.disabled)")
      if (option) this.selectValue(option.dataset.value)
      const group = event.target.closest("[data-group]")
      if (group) this.toggleGroup(group)
    })
    if (this.selectAll)
      this.listen(this.selectAll, "click", (event) => {
        event.stopPropagation()
        this.toggleOptions(
          this.config.selectAllMode === "filtered" || this.externalSearch()
            ? this.nativeOptions(this.visibleOptions())
            : [...this.select.options].filter((option) => !this.isDisabled(option)),
        )
      })
    if (this.search)
      this.listen(this.search, "input", () => {
        this.updateSearchSize(this.search.value.length + 1)
        this.filter(this.search.value)
        this.show()
      })
    this.listen(this.select, "invalid", (event) => {
      event.preventDefault()
      this.show()
      const input = this.search || this.toggler
      input.setAttribute("aria-invalid", "true")
      if (this.search) {
        this.search.setCustomValidity("Choose at least one option.")
        this.search.reportValidity()
      }
      input.focus()
    })
    this.listen(this.selection, "click", (event) => {
      const remove = event.target.closest(".form-multi-select-tag-delete")
      if (remove) {
        event.stopPropagation()
        this.selectValue(remove.parentElement.dataset.value, false)
      }
    })
    this.listen(document, "click", (event) => {
      if (!event.composedPath().includes(this.wrapper)) this.hide()
    })
    this.listen(this.wrapper, "keydown", (event) => this.handleKeydown(event))
  }

  visibleOptions() {
    return [...this.options.querySelectorAll(".form-multi-select-option:not(.disabled)")].filter(
      (option) => option.style.display !== "none",
    )
  }
  nativeOptions(options) {
    return options.map((option) => [...this.select.options].find((native) => native.value === option.dataset.value))
  }

  toggleGroup(group) {
    const options = this.nativeOptions([
      ...group.parentElement.querySelectorAll(".form-multi-select-option:not(.disabled)"),
    ])
    this.toggleOptions(options, !(options.length && options.every((option) => option.selected)))
  }

  toggleOptions(options, force = null) {
    const selected = force ?? !this.allSelected(options)
    let limited = false
    options.forEach((option) => {
      if (this.isDisabled(option)) return
      if (
        selected &&
        !option.selected &&
        this.hasSelectionLimit() &&
        this.selectedOptions.length >= this.config.selectionLimit
      )
        limited = true
      else {
        if (selected && !this.config.multiple) this.select.selectedIndex = -1
        if (!selected && !this.config.multiple && option.selected) this.select.selectedIndex = -1
        else option.selected = selected
      }
    })
    this.refresh()
    if (limited) this.emit("selectionLimit", { selectionLimit: this.config.selectionLimit })
  }

  allSelected(options) {
    const selected =
      this.config.selectAllMode === "filtered" || this.externalSearch()
        ? options.filter((option) => option.selected).length
        : this.selectedOptions.length
    const selectedElsewhere = this.externalSearch() ? this.selectedOptions.length - selected : 0
    const target = this.hasSelectionLimit()
      ? Math.min(options.length, this.config.selectionLimit - selectedElsewhere)
      : options.length
    return target > 0 && selected >= target
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.hide()
      ;(this.search || this.toggler).focus()
      return
    }
    if (["Backspace", "Delete"].includes(event.key) && event.target === this.search && !this.search.value) {
      const last = this.selectedOptions.findLast((option) => !this.isDisabled(option))
      if (last) this.selectValue(last.value, false)
      return
    }
    const option = event.target.closest(".form-multi-select-option, [data-group]")
    if (["Enter", " "].includes(event.key) && option) {
      event.preventDefault()
      option.click()
      return
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault()
      this.show()
      const options = this.visibleOptions()
      const index = options.indexOf(event.target)
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? options.length - 1
            : index === -1
              ? event.key === "ArrowDown"
                ? 0
                : options.length - 1
              : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length
      options[next]?.focus()
    } else if ((event.key === "Enter" || event.key === " ") && event.target === this.toggler) {
      event.preventDefault()
      this.toggle()
    }
  }

  selectValue(value, force = null) {
    const nativeOption = [...this.select.options].find((option) => option.value === value)
    if (!nativeOption || this.isDisabled(nativeOption)) return
    const selected = force === null ? !this.config.multiple || !nativeOption.selected : force
    const limited =
      selected &&
      !nativeOption.selected &&
      this.hasSelectionLimit() &&
      this.selectedOptions.length >= this.config.selectionLimit
    if (limited) this.emit("selectionLimit", { selectionLimit: this.config.selectionLimit })
    else {
      if (!this.config.multiple)
        [...this.select.options].forEach((option) => {
          option.selected = false
        })
      if (!selected && !this.config.multiple) this.select.selectedIndex = -1
      else nativeOption.selected = selected
      this.refresh()
    }
    if (!this.config.multiple) this.hide()
    if (force === null && this.config.clearSearchOnSelect && this.search) {
      this.search.value = ""
      this.filter("")
      this.search.focus()
    }
  }

  refresh() {
    this.options.querySelectorAll(".form-multi-select-option").forEach((option) => {
      const selected = [...this.select.options].some(
        (nativeOption) => nativeOption.value === option.dataset.value && nativeOption.selected,
      )
      option.classList.toggle("form-multi-selected", selected)
      option.setAttribute("aria-selected", selected)
    })
    this.updateSelection()
    this.search?.setCustomValidity("")
    ;(this.search || this.toggler).removeAttribute("aria-invalid")
    this.select.dispatchEvent(new Event("change", { bubbles: true }))
    this.emit("changed", {
      value: this.selectedOptions.map((option) => ({
        value: option.value,
        text: option.textContent,
        ...(this.isDisabled(option) ? { disabled: true } : {}),
      })),
    })
    this.select.querySelectorAll("option[data-retained-selection]").forEach((option) => {
      if (!option.selected) option.remove()
    })
  }

  updateSelection() {
    const options = this.selectedOptions
    this.selection.querySelectorAll(":scope > :not(.form-multi-select-search)").forEach((element) => element.remove())
    if (!options.length && !this.search) {
      const placeholder = document.createElement("span")
      placeholder.className = "form-multi-select-placeholder"
      placeholder.textContent = this.config.placeholder
      this.selection.prepend(placeholder)
    } else if (options.length && (!this.config.multiple || this.config.selectionType === "counter")) {
      if (!this.search) {
        const counter = document.createElement("span")
        counter.textContent = this.config.multiple ? `${options.length} item(s) selected` : options[0].textContent
        this.selection.prepend(counter)
      }
    } else if (this.config.multiple && this.config.selectionType === "tags") {
      options.forEach((option) => {
        const tag = document.createElement("div")
        tag.className = "form-multi-select-tag"
        tag.dataset.value = option.value
        tag.textContent = option.textContent
        if (!this.isDisabled(option)) {
          const remove = document.createElement("button")
          remove.type = "button"
          remove.className = "form-multi-select-tag-delete"
          remove.setAttribute("aria-label", `Remove ${option.textContent}`)
          tag.append(remove)
        }
        this.selection.insertBefore(tag, this.search || null)
      })
    } else if (options.length) {
      const text = document.createElement("span")
      text.textContent = options.map((option) => option.textContent).join(", ")
      this.selection.prepend(text)
    }
    this.cleaner?.classList.toggle("d-none", options.length === 0)
    if (this.search) {
      if (!options.length) this.search.placeholder = this.config.placeholder
      else if (!this.config.multiple) this.search.placeholder = options[0].textContent
      else if (this.config.selectionType === "counter") this.search.placeholder = `${options.length} item(s) selected`
      else this.search.removeAttribute("placeholder")
      this.updateSearchSize()
    }
    this.updateHeader()
  }

  updateSearchSize(size = 2) {
    if (!this.search || !this.config.multiple || this.config.selectionType === "counter") return
    if (this.selectedOptions.length) this.search.size = size
    else this.search.removeAttribute("size")
  }

  updateHeader() {
    for (const group of this.options.querySelectorAll("[data-group]")) {
      const options = this.nativeOptions([
        ...group.parentElement.querySelectorAll(".form-multi-select-option:not(.disabled)"),
      ])
      const selected = options.filter((option) => option.selected).length
      group.classList.toggle("form-multi-selected", options.length > 0 && selected === options.length)
      group.classList.toggle("form-multi-select-indeterminate", selected > 0 && selected < options.length)
    }
    if (!this.header) return
    if (this.config.headerTemplate) {
      setTemplate(
        this.header,
        this.config.headerTemplate(
          {
            selected: this.selectedOptions.length,
            total: this.options.querySelectorAll(".form-multi-select-option:not(.disabled)").length,
            filtered: this.visibleOptions().length,
            filteredSelected: this.nativeOptions(this.visibleOptions()).filter((option) => option.selected).length,
          },
          {
            selectAll: () => this.toggleOptions([...this.select.options], true),
            selectFiltered: () => this.toggleOptions(this.nativeOptions(this.visibleOptions()), true),
            deselectAll: () => this.clear(),
            deselectFiltered: () => this.toggleOptions(this.nativeOptions(this.visibleOptions()), false),
          },
        ),
      )
    } else if (this.selectAll) {
      const options =
        this.config.selectAllMode === "filtered" || this.externalSearch()
          ? this.nativeOptions(this.visibleOptions())
          : [...this.select.options].filter((option) => !this.isDisabled(option))
      const selected = this.allSelected(options)
      const filtered =
        this.config.selectAllMode === "filtered" &&
        this.visibleOptions().length < this.options.querySelectorAll(".form-multi-select-option:not(.disabled)").length
      this.selectAll.textContent = filtered
        ? selected
          ? "Deselect filtered"
          : "Select filtered"
        : selected
          ? "Deselect all"
          : "Select all"
      this.selectAll.classList.toggle("form-multi-selected", selected)
      this.selectAll.classList.toggle(
        "form-multi-select-indeterminate",
        !selected &&
          (this.config.selectAllMode === "filtered" || this.externalSearch()
            ? options.some((option) => option.selected)
            : this.selectedOptions.length > 0),
      )
    }
  }

  filter(query, emit = true) {
    const value = query.trim().toLowerCase()
    this.options.querySelectorAll(".form-multi-select-option").forEach((option) => {
      option.style.display = this.externalSearch() || option.textContent.toLowerCase().includes(value) ? "" : "none"
    })
    this.options.querySelectorAll(".form-multi-select-optgroup").forEach((group) => {
      group.hidden = ![...group.querySelectorAll(".form-multi-select-option")].some(
        (option) => option.style.display !== "none",
      )
    })
    this.options.querySelector(".form-multi-select-options-empty")?.remove()
    const hasResults = [...this.options.querySelectorAll(".form-multi-select-option")].some(
      (option) => option.style.display !== "none",
    )
    if (!hasResults) {
      const empty = document.createElement("div")
      empty.className = "form-multi-select-options-empty"
      empty.textContent = this.config.searchNoResultsLabel || "No results found"
      empty.setAttribute("role", "status")
      this.options.append(empty)
    }
    if (this.header && !this.config.headerTemplate) this.header.hidden = !hasResults
    this.updateHeader()
    if (emit) this.emit("search", { value: query })
  }

  deselectAll() {
    this.clear()
  }

  clear() {
    this.toggleOptions(this.selectedOptions, false)
  }

  show() {
    if (this.wrapper.classList.contains("show") || this.config.disabled || this.select.disabled) return
    if (this.emit("show", {}, { cancelable: true }).defaultPrevented) return
    this.wrapper.classList.add("show")
    this.menu.classList.add("show")
    this.toggler.setAttribute("aria-expanded", "true")
    this.popper = createPopper(this.toggler, this.menu, {
      placement: "bottom-start",
      modifiers: [{ name: "offset", options: { offset: [0, 2] } }],
    })
  }

  hide() {
    if (!this.wrapper.classList.contains("show")) return
    const refocus = this.wrapper.contains(document.activeElement)
    if (this.search) {
      this.search.value = ""
      this.updateSearchSize()
      this.filter("")
    }
    this.wrapper.classList.remove("show")
    this.menu.classList.remove("show")
    this.toggler.setAttribute("aria-expanded", "false")
    this.popper?.destroy()
    this.popper = null
    if (refocus && !this.config.disabled && !this.select.disabled) (this.search || this.toggler).focus()
  }

  toggle() {
    this.wrapper.classList.contains("show") ? this.hide() : this.show()
  }

  dispose() {
    if (this.signal.aborted) return
    this.popper?.destroy()
    if (this.originalTabIndex === null) this.select.removeAttribute("tabindex")
    else this.select.setAttribute("tabindex", this.originalTabIndex)
    if (this.originalName === null) this.select.removeAttribute("name")
    else this.select.name = this.originalName
    this.select.required = this.originalRequired
    this.select.multiple = this.originalMultiple
    if (this.label?.id === this.generatedLabelId) this.label.removeAttribute("id")
    this.select.className = this.originalClassName
    if (this.config.options) this.select.innerHTML = this.originalHTML
    if (this.originalNextSibling?.parentNode === this.originalParent)
      this.originalParent.insertBefore(this.select, this.originalNextSibling)
    else this.originalParent.append(this.select)
    this.wrapper.remove()
    super.dispose()
  }
  snapshotState() {
    const options = [...this.select.children].map((child) => {
      const option = (item) => ({
        ...this.optionData.get(item.value),
        value: item.value,
        text: item.text,
        selected: item.selected,
        disabled: item.disabled,
      })
      return child.tagName === "OPTGROUP"
        ? { label: child.label, disabled: child.disabled, options: [...child.children].map(option) }
        : option(child)
    })
    return {
      config: { options, value: this.selectedOptions.map((option) => option.value) },
      order: this.selectionOrder,
    }
  }
  restoreState(state) {
    if (state.order) {
      this.selectionOrder = state.order
      this.updateSelection()
    }
  }
}
